import React, { useState, useEffect } from 'react';
import { Editor } from '@tiptap/react';
import { Transaction } from '@tiptap/pm/state';
import { TableMap } from 'prosemirror-tables'; 
import { Dialog } from '../dialogs/dialog';
import { Button } from 'primereact/button';
import { TabView, TabPanel } from 'primereact/tabview';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { narrowTableMode } from '../../general/table-responsive';
import type { NarrowTableMode } from '../../general/table-responsive';

interface TablePropertiesDialogProps {
    editor: Editor;
    visible: boolean;
    onHide: () => void;
}

interface TableDialogState {
    table: {
        style: string | null;
        tableWidth: string;
        tableAlign: string;
        cellPadding: string;
        cellSpacing: string;
        borderWidth: string;
        borderStyle: string;
        stylePreset: string;
        borderCollapse: string;
        stripedRows: boolean;
        caption: string;
        narrowScreen: NarrowTableMode;
    };
    row: {
        style: string | null;
        rowHeight: string;
        rowStyle: string;
        hAlign: string;
        vAlign: string;
    };
    column: {
        colWidth: string;
        colStyle: string;
        hAlign: string;
        vAlign: string;
    };
    cell: {
        style: string | null;
        cellStyle: string;
        hAlign: string;
        vAlign: string;
    };
}

const ALIGN_OPTIONS = [
    { label: 'Left', value: 'left' },
    { label: 'Center', value: 'center' },
    { label: 'Right', value: 'right' }
];

const VALIGN_OPTIONS = [
    { label: 'Top', value: 'top' },
    { label: 'Middle', value: 'middle' },
    { label: 'Bottom', value: 'bottom' }
];

const BORDER_STYLES = [
    { label: 'Solid', value: 'solid' },
    { label: 'Dashed', value: 'dashed' },
    { label: 'Dotted', value: 'dotted' },
    { label: 'Double', value: 'double' },
    { label: 'None', value: 'none' }
];

const PRESET_OPTIONS = [
    { label: 'Default', value: 'default' },
    { label: 'Primary', value: 'primary' },
    { label: 'Info', value: 'info' },
    { label: 'Success', value: 'success' },
    { label: 'Warning', value: 'warning' },
    { label: 'Danger', value: 'danger' },
    { label: 'Muted', value: 'muted' }
];

const defaultValues: TableDialogState = {
    table: { style: null, tableWidth: '100%', tableAlign: 'left', cellPadding: '8px', cellSpacing: '0px', borderWidth: '1px', borderStyle: 'solid', stylePreset: 'default', borderCollapse: 'separate', stripedRows: false, caption: '', narrowScreen: null },
    row: { style: null, rowHeight: 'auto', rowStyle: 'default', hAlign: 'left', vAlign: 'middle' },
    column: { colWidth: 'auto', colStyle: 'default', hAlign: 'left', vAlign: 'middle' },
    cell: { style: null, cellStyle: 'default', hAlign: 'left', vAlign: 'middle' }
};

export const TablePropertiesDialog: React.FC<TablePropertiesDialogProps> = ({ editor, visible, onHide }) => {
    const [tableValues, setTableValues] = useState<TableDialogState>(defaultValues);
    const [initialTableValues, setInitialTableValues] = useState<TableDialogState | null>(null);
    const [activeIndex, setActiveIndex] = useState(0);

    const updateValue = (category: keyof TableDialogState, key: string, value: any) => {
        setTableValues(prev => ({
            ...prev,
            [category]: {
                ...prev[category],
                [key]: value
            }
        }));
    };

    const updateData = () => {
        if (!editor) return;
        const { state } = editor;
        const { $from } = state.selection;

        let tableNode = null, rowNode = null, cellNode = null;
        let tablePos = -1;

        // Traverse upwards to find table, row, and cell
        for (let d = $from.depth; d > 0; d--) {
            const node = $from.node(d);
            if (node.type.name === 'table') {
                tableNode = node;
                tablePos = $from.before(d);
            }
            if (node.type.name === 'table_row') rowNode = node;
            if (node.type.name === 'table_cell' || node.type.name === 'table_header') cellNode = node;
        }

        if (!tableNode || !rowNode || !cellNode) return;

        const snapshot: TableDialogState = {
            table: {
                style: tableNode.attrs.style || null,
                tableWidth: tableNode.attrs.tableWidth || '100%',
                tableAlign: tableNode.attrs.tableAlign || 'left',
                cellPadding: tableNode.attrs.cellPadding || '8px',
                cellSpacing: tableNode.attrs.cellSpacing || '0px',
                borderWidth: tableNode.attrs.borderWidth || '1px',
                borderStyle: tableNode.attrs.borderStyle || 'solid',
                stylePreset: tableNode.attrs.stylePreset || 'default',
                borderCollapse: tableNode.attrs.borderCollapse || 'separate',
                stripedRows: !!tableNode.attrs.stripedRows,
                caption: tableNode.attrs.caption || '',
                narrowScreen: narrowTableMode(tableNode.attrs.narrowScreen),
            },
            row: {
                style: rowNode.attrs.style || null,
                rowHeight: rowNode.attrs.rowHeight || 'auto',
                rowStyle: rowNode.attrs.rowStyle || 'default',
                hAlign: rowNode.attrs.hAlign || 'left',
                vAlign: rowNode.attrs.vAlign || 'middle',
            },
            column: {
                colWidth: cellNode.attrs.colWidth || 'auto',
                colStyle: cellNode.attrs.colStyle || 'default',
                hAlign: cellNode.attrs.hAlign || 'left',
                vAlign: cellNode.attrs.vAlign || 'middle',
            },
            cell: {
                style: cellNode.attrs.style || null,
                cellStyle: cellNode.attrs.cellStyle || 'default',
                hAlign: cellNode.attrs.hAlign || 'left',
                vAlign: cellNode.attrs.vAlign || 'middle',
            }
        };

        setTableValues(snapshot);
        setInitialTableValues(snapshot);
    };

    useEffect(() => {
        if (visible) {
            updateData();
            setActiveIndex(0);
        }
    }, [visible]);

    const applyAll = () => {
        if (!initialTableValues) return;
        const chain = editor.chain().focus() as any;

        const getDiff = (category: keyof TableDialogState) => {
            const current = tableValues[category] as Record<string, any>;
            const initial = initialTableValues[category] as Record<string, any>;
            const diff: Record<string, any> = {};
            Object.keys(current).forEach((key) => {
                if (current[key] !== initial[key]) {
                    diff[key] = current[key];
                }
            });
            return diff;
        };

        const tDiff = getDiff('table');
        const rDiff = getDiff('row');
        const cDiff = getDiff('column');
        const cellDiff = getDiff('cell');

        if (Object.keys(tDiff).length > 0) chain.updateAttributes('table', tDiff);
        if (Object.keys(rDiff).length > 0) chain.updateAttributes('table_row', rDiff);
        
        if (Object.keys(cDiff).length > 0) {
            const { selection } = editor.state;
            let columnIndex = -1;

            // 1. Find the current column index
            editor.state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
                if (node.type.name === 'table_cell' || node.type.name === 'table_header') {
                    const $pos = editor.state.doc.resolve(pos);
                    // At a cell boundary, the parent is its row. TableMap accounts
                    // for cells spanning earlier columns/rows in the visual grid.
                    const tableDepth = $pos.depth - 1;
                    columnIndex = TableMap.get($pos.node(tableDepth))
                        .findCell(pos - $pos.start(tableDepth)).left;
                    return false;
                }
            });

            // 2. Iterate and update all cells in that column index
            if (columnIndex !== -1) {
                chain.command(({ tr }: { tr: Transaction }) => {
                    const { selection } = editor.state;
                    let tablePos = -1;
                    let tableNode = null;

                    // Find the table start
                    editor.state.doc.nodesBetween(selection.from, selection.to, (node, pos) => {
                        if (node.type.name === 'table') {
                            tablePos = pos;
                            tableNode = node;
                            return false; 
                        }
                    });

                    if (tableNode && tablePos !== -1) {
                        const map = TableMap.get(tableNode);
                        const cells = map.cellsInRect({ 
                            left: columnIndex, 
                            right: columnIndex + 1, 
                            top: 0, 
                            bottom: map.height 
                        });

                        // Apply attributes to the specific column cells
                        Object.entries(cDiff).forEach(([key, value]) => {
                            cells.forEach(cellPos => {
                                const absoluteCellPos = tablePos + cellPos + 1;
                                const cellNode = tr.doc.nodeAt(absoluteCellPos);

                                if (cellNode) {
                                    tr.setNodeMarkup(absoluteCellPos, undefined, {
                                        ...cellNode.attrs,
                                        [key]: value,
                                    });
                                }
                            });
                        });
                    }
                    return true; 
                });
            }
        }
        
        if (Object.keys(cellDiff).length > 0) {
            chain.updateAttributes('table_cell', cellDiff);
            chain.updateAttributes('table_header', cellDiff);
        }

        chain.run();
        onHide();
    };

    const renderUnitGroup = (label: string, value: string | null, category: keyof TableDialogState, key: string) => {
        // 1. Detect corrupted "NaN" or "null" strings
        const isCorrupted = !value || value.includes('NaN') || value.includes('null') || value === '';
        
        // 2. Safely parse
        const numPart = isCorrupted ? null : parseInt(value);
        
        // 3. Ensure unitPart is never "NaN"
        const unitPart = isCorrupted ? 'px' : (value.replace(/[0-9]/g, '') || 'px');

        return (
            <div className="flex flex-column gap-1">
                <label className="font-semibold text-sm">{label}</label>
                <div className="p-inputgroup flex-1">
                    <InputNumber 
                        value={numPart} 
                        placeholder="auto" 
                        onValueChange={(e) => {
                            if (e.value === null) {
                                updateValue(category, key, null);
                            } else {
                                updateValue(category, key, `${e.value}${unitPart}`);
                            }
                        }} 
                        mode="decimal"
                    />
                    <Dropdown 
                        value={unitPart} 
                        options={['px', '%']} 
                        onChange={(e) => {
                            if (numPart !== null) {
                                updateValue(category, key, `${numPart}${e.value}`);
                            }
                        }} 
                        className="w-6rem" 
                    />
                </div>
            </div>
        );
    };

    const clearAllStyles = () => {
        const { state } = editor;
        const { selection, doc } = state;
        let tablePos = -1;
        let tableNode: any = null; // Added explicit type to prevent lint error

        // 1. Find the parent table
        doc.nodesBetween(selection.from, selection.to, (node, pos) => {
            if (node.type.name === 'table') {
                tablePos = pos;
                tableNode = node;
                return false;
            }
        });

        if (!tableNode || tablePos === -1) return;

        const chain = editor.chain().focus();

        // 2. Reset the Table Node (Screen 5 Defaults)
        chain.command(({ tr }: { tr: Transaction }) => {
            // Reset Table attributes
            tr.setNodeMarkup(tablePos, undefined, {
                tableWidth: '100%',
                tableAlign: 'left',
                cellPadding: '8px',
                cellSpacing: '0px',
                borderWidth: '1px',
                borderStyle: 'solid',
                stylePreset: 'default',
                borderCollapse: 'separate',
                stripedRows: false,
                caption: null,
                narrowScreen: null,
            });

            // 3. Iterate through every child of the table (Rows)
            tableNode.forEach((row: any, rowOffset: number) => {
                const absoluteRowPos = tablePos + rowOffset + 1;
                
                // Reset Row attributes (Screen 6 Defaults)
                tr.setNodeMarkup(absoluteRowPos, undefined, {
                    rowHeight: null,
                    rowStyle: 'default',
                    hAlign: 'left',
                    vAlign: 'middle',
                });

                // 4. Iterate through every cell in the row (TD/TH)
                row.forEach((cell: any, cellOffset: number) => {
                    const absoluteCellPos = absoluteRowPos + cellOffset + 1;
                    
                    // Reset Cell attributes (Screen 7 Defaults)
                    tr.setNodeMarkup(absoluteCellPos, undefined, {
                        colspan: cell.attrs.colspan || 1,
                        rowspan: cell.attrs.rowspan || 1,
                        colWidth: null,
                        colwidth: null, 
                        style: null, 
                        colStyle: 'default',
                        cellStyle: 'default',
                        hAlign: 'left',
                        vAlign: 'middle',
                    });
                });
            });

            return true;
        });

        chain.run();
        updateData();
        onHide();
    };

    return (
        <Dialog 
            header="Table properties" 
            visible={visible} 
            style={{ width: '650px', height: '680px' }} 
            onHide={onHide} 
            footer={(
                <div className="flex justify-content-between w-full">
                    <Button 
                        label="Reset to default" 
                        icon="pi pi-refresh" 
                        className="p-button-text p-button-danger" 
                        onClick={clearAllStyles} 
                    />
                    <div>
                        <Button label="Cancel" icon="pi pi-times" onClick={onHide} className="p-button-text p-button-secondary" />
                        <Button label="Apply" icon="pi pi-check" onClick={applyAll} autoFocus />
                    </div>
                </div>
            )}
        >
            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Table">
                    <div className="grid p-fluid mt-2">
                        <div className="col-12 md:col-6">
                            {renderUnitGroup("Table width", tableValues.table.tableWidth, 'table', 'tableWidth')}
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="font-semibold block mb-1 text-sm">Table align</label>
                            <Dropdown 
                                value={tableValues.table.tableAlign} 
                                options={ALIGN_OPTIONS} 
                                onChange={(e) => updateValue('table', 'tableAlign', e.value)} 
                            />
                        </div>
                        <div className="col-12">
                            <label htmlFor="table-narrow-screen" className="font-semibold block mb-1 text-sm">Narrow-screen behaviour</label>
                            <Dropdown inputId="table-narrow-screen" value={tableValues.table.narrowScreen} showClear placeholder="Use existing layout"
                                options={[{ label: 'Fit and wrap', value: 'fit' }, { label: 'Scroll horizontally', value: 'scroll' }]}
                                onChange={event => updateValue('table', 'narrowScreen', narrowTableMode(event.value))} />
                            <small className="block mt-1">Fit and wrap applies at 640px and below and allows taller rows. Scrolling keeps columns together whenever the table exceeds the available width, with a minimum table width of 640px on mobile. Clear to use the existing layout.</small>
                        </div>
                        <div className="col-12 md:col-6">
                            {renderUnitGroup("Cell padding", tableValues.table.cellPadding, 'table', 'cellPadding')}
                        </div>
                        <div className="col-12 md:col-6">
                            {renderUnitGroup("Cell spacing", tableValues.table.cellSpacing, 'table', 'cellSpacing')}
                        </div>
                        <div className="col-12 md:col-4">
                            {renderUnitGroup("Border width", tableValues.table.borderWidth, 'table', 'borderWidth')}
                        </div>
                        <div className="col-12 md:col-4">
                            <label className="font-semibold block mb-1 text-sm">Border style</label>
                            <Dropdown 
                                value={tableValues.table.borderStyle} 
                                options={BORDER_STYLES} 
                                onChange={(e) => updateValue('table', 'borderStyle', e.value)} 
                            />
                        </div>
                        <div className="col-12 md:col-4">
                            <label className="font-semibold block mb-1 text-sm">Preset</label>
                            <Dropdown 
                                value={tableValues.table.stylePreset} 
                                options={PRESET_OPTIONS} 
                                onChange={(e) => updateValue('table', 'stylePreset', e.value)} 
                            />
                        </div>
                        <div className="col-12 flex gap-4 mt-2">
                            <div className="flex align-items-center">
                                <Checkbox 
                                    inputId="c1" 
                                    checked={tableValues.table.borderCollapse === 'collapse'} 
                                    onChange={e => updateValue('table', 'borderCollapse', e.checked ? 'collapse' : 'separate')} 
                                />
                                <label htmlFor="c1" className="ml-2 text-sm">Collapse borders</label>
                            </div>
                            <div className="flex align-items-center">
                                <Checkbox 
                                    inputId="c2" 
                                    checked={tableValues.table.stripedRows} 
                                    onChange={e => updateValue('table', 'stripedRows', e.checked ?? false)} 
                                />
                                <label htmlFor="c2" className="ml-2 text-sm">Striped rows</label>
                            </div>
                        </div>
                        <div className="col-12">
                            <label className="font-semibold block mb-1 text-sm">Table caption</label>
                            <InputTextarea
                                value={tableValues.table.caption} 
                                onChange={(e) => updateValue('table', 'caption', e.target.value)} 
                                rows={2} 
                                className="w-full"
                                placeholder="Enter table description..."
                            />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel header="Row">
                    <div className="grid p-fluid mt-2">
                        <div className="col-12 md:col-6">
                            {renderUnitGroup("Row height", tableValues.row.rowHeight, 'row', 'rowHeight')}
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="font-semibold block mb-1 text-sm">Row style</label>
                            <Dropdown value={tableValues.row.rowStyle} options={PRESET_OPTIONS} onChange={(e) => updateValue('row', 'rowStyle', e.value)} />
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="font-semibold block mb-1 text-sm">Horizontal align</label>
                            <Dropdown value={tableValues.row.hAlign} options={ALIGN_OPTIONS} onChange={(e) => updateValue('row', 'hAlign', e.value)} />
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="font-semibold block mb-1 text-sm">Vertical align</label>
                            <Dropdown value={tableValues.row.vAlign} options={VALIGN_OPTIONS} onChange={(e) => updateValue('row', 'vAlign', e.value)} />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel header="Column">
                    <div className="grid p-fluid mt-2">
                        <div className="col-12 md:col-6">
                            {renderUnitGroup("Column width", tableValues.column.colWidth, 'column', 'colWidth')}
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="font-semibold block mb-1 text-sm">Column style</label>
                            <Dropdown value={tableValues.column.colStyle} options={PRESET_OPTIONS} onChange={(e) => updateValue('column', 'colStyle', e.value)} />
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="font-semibold block mb-1 text-sm">Horizontal align</label>
                            <Dropdown value={tableValues.column.hAlign} options={ALIGN_OPTIONS} onChange={(e) => updateValue('column', 'hAlign', e.value)} />
                        </div>
                        <div className="col-12 md:col-6">
                            <label className="font-semibold block mb-1 text-sm">Vertical align</label>
                            <Dropdown value={tableValues.column.vAlign} options={VALIGN_OPTIONS} onChange={(e) => updateValue('column', 'vAlign', e.value)} />
                        </div>
                    </div>
                </TabPanel>

                <TabPanel header="Cell">
                    <div className="grid p-fluid mt-2">
                        <div className="col-12 md:col-4">
                            <label className="font-semibold block mb-1 text-sm">Cell style</label>
                            <Dropdown value={tableValues.cell.cellStyle} options={PRESET_OPTIONS} onChange={(e) => updateValue('cell', 'cellStyle', e.value)} />
                        </div>
                        <div className="col-12 md:col-4">
                            <label className="font-semibold block mb-1 text-sm">Horizontal align</label>
                            <Dropdown value={tableValues.cell.hAlign} options={ALIGN_OPTIONS} onChange={(e) => updateValue('cell', 'hAlign', e.value)} />
                        </div>
                        <div className="col-12 md:col-4">
                            <label className="font-semibold block mb-1 text-sm">Vertical align</label>
                            <Dropdown value={tableValues.cell.vAlign} options={VALIGN_OPTIONS} onChange={(e) => updateValue('cell', 'vAlign', e.value)} />
                        </div>
                    </div>
                </TabPanel>
            </TabView>
        </Dialog>
    );
};
